if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.stage = this.querySelector('.product-media-modal__content');
      this.zoom = 1;
      this.pan = { x: 0, y: 0 };
      this.pointers = new Map();
      this.mediaItems = Array.from(this.stage.children).filter((element) => element.hasAttribute('data-media-id'));
      this.activeIndex = 0;
      this.thumbnails = Array.from(this.querySelectorAll('[data-hissa-media-thumbnail]'));
      this.querySelector('[data-hissa-media-previous]')?.addEventListener('click', () => this.navigate(-1));
      this.querySelector('[data-hissa-media-next]')?.addEventListener('click', () => this.navigate(1));
      this.thumbnails.forEach((button) => button.addEventListener('click', () => this.showActiveMedia(button.dataset.mediaId)));
      this.addEventListener('keydown', (event) => {
        if (!this.hasAttribute('open') || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'Tab') {
          this.trapGalleryTab(event);
          return;
        }
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target.closest('input, textarea, select, video, iframe, model-viewer, [contenteditable="true"]')) return;
        event.preventDefault();
        event.stopPropagation();
        this.navigate(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.querySelector('[data-hissa-zoom-in]')?.addEventListener('click', () => this.setZoom(this.zoom + 0.5));
      this.querySelector('[data-hissa-zoom-out]')?.addEventListener('click', () => this.setZoom(this.zoom - 0.5));
      this.querySelector('[data-hissa-zoom-reset]')?.addEventListener('click', () => this.setZoom(1));
      this.stage.addEventListener('dblclick', (event) => {
        if (!this.activeImage) return;
        event.preventDefault();
        this.setZoom(this.zoom > 1 ? 1 : 2);
      });
      this.stage.addEventListener('wheel', (event) => {
        if (!this.activeImage || event.deltaY === 0) return;
        event.preventDefault();
        this.setZoom(this.zoom + (event.deltaY < 0 ? 0.25 : -0.25));
      }, { passive: false });
      this.stage.addEventListener('pointerdown', (event) => this.pointerDown(event));
      this.stage.addEventListener('pointermove', (event) => this.pointerMove(event));
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((name) => {
        this.stage.addEventListener(name, (event) => this.pointerEnd(event));
      });
      this.resizeObserver = new ResizeObserver(() => this.paintZoom());
      this.resizeObserver.observe(this.stage);
    }
    show(opener) {
      if (this.hasAttribute('open')) return;
      this.pageY = window.scrollY;
      this.previousBodyTop = document.body.style.top;
      super.show(opener);
      document.body.style.top = `-${this.pageY}px`;
      document.body.classList.add('hissa-gallery-open');
      this.showActiveMedia();
    }
    hide() {
      if (!this.hasAttribute('open')) return;
      this.pointers.clear();
      this.gesture = null;
      this.swipeStart = null;
      this.setZoom(1);
      super.hide();
      document.body.classList.remove('hissa-gallery-open');
      document.body.style.top = this.previousBodyTop || '';
      const previousBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, this.pageY || 0);
      document.documentElement.style.scrollBehavior = previousBehavior;
    }
    showActiveMedia(mediaId) {
      const id = mediaId || this.openedBy?.getAttribute('data-media-id');
      const active = this.mediaItems.find((element) => element.getAttribute('data-media-id') === id) || this.mediaItems[0];
      this.activeIndex = Math.max(0, this.mediaItems.indexOf(active));
      this.mediaItems.forEach((element) => {
        const selected = element === active;
        element.classList.toggle('active', selected);
        element.hidden = !selected;
        element.inert = !selected;
        element.setAttribute('aria-hidden', String(!selected));
      });
      this.pointers.clear();
      this.gesture = null;
      this.swipeStart = null;
      window.pauseAllMedia();
      this.activeImage = active?.tagName === 'IMG' ? active : null;
      if (this.activeImage) this.activeImage.draggable = false;
      const controls = this.querySelector('.hissa-gallery-controls');
      if (controls) controls.hidden = !this.activeImage;
      this.querySelectorAll('[data-hissa-media-previous], [data-hissa-media-next]').forEach((button) => {
        button.hidden = this.mediaItems.length < 2;
      });
      const counter = this.querySelector('[data-hissa-media-counter]');
      if (counter) counter.textContent = `${this.mediaItems.length ? this.activeIndex + 1 : 0} / ${this.mediaItems.length}`;
      this.thumbnails.forEach((button) => {
        const selected = button.dataset.mediaId === active?.getAttribute('data-media-id');
        button.setAttribute('aria-current', String(selected));
        button.classList.toggle('is-active', selected);
      });
      const thumbnail = this.thumbnails.find((button) => button.classList.contains('is-active'));
      const album = this.querySelector('[data-hissa-gallery-album]');
      if (thumbnail && album) album.scrollTo({ left: thumbnail.offsetLeft - (album.clientWidth - thumbnail.offsetWidth) / 2, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth' });
      this.setZoom(1);
      this.stage.scrollTop = 0;
      this.stage.scrollLeft = 0;
      if (active?.tagName === 'DEFERRED-MEDIA') active.loadContent();
      if (this.activeImage && !this.activeImage.complete) this.activeImage.addEventListener('load', () => this.paintZoom(), { once: true });
    }
    trapGalleryTab(event) {
      const focusable = Array.from(this.querySelectorAll('button, a[href], input, select, textarea, summary, iframe, [tabindex]')).filter((element) => element.tabIndex >= 0 && !element.disabled && !element.closest('[hidden], [inert]') && element.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      if (!first) {
        event.preventDefault();
        event.stopPropagation();
        this.querySelector('[role="dialog"]')?.focus();
      } else if (!focusable.includes(current) || (event.shiftKey ? current === first : current === last)) {
        event.preventDefault();
        event.stopPropagation();
        (event.shiftKey ? last : first).focus();
      }
    }
    navigate(direction) {
      if (this.mediaItems.length < 2) return;
      const index = (this.activeIndex + direction + this.mediaItems.length) % this.mediaItems.length;
      this.showActiveMedia(this.mediaItems[index].getAttribute('data-media-id'));
    }
    setZoom(value) {
      this.zoom = Math.max(1, Math.min(4, value));
      if (this.zoom <= 1.01) {
        this.zoom = 1;
        this.pan = { x: 0, y: 0 };
      }
      this.paintZoom();
    }
    paintZoom() {
      if (!this.stage) return;
      if (this.activeImage) {
        const style = getComputedStyle(this.stage);
        const width = Math.max(0, this.stage.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0));
        const height = Math.max(0, this.stage.clientHeight - (parseFloat(style.paddingTop) || 0) - (parseFloat(style.paddingBottom) || 0));
        const naturalWidth = this.activeImage.naturalWidth || Number(this.activeImage.getAttribute('width'));
        const naturalHeight = this.activeImage.naturalHeight || Number(this.activeImage.getAttribute('height'));
        let imageWidth = this.activeImage.offsetWidth;
        let imageHeight = this.activeImage.offsetHeight;
        if (Number.isFinite(naturalWidth) && Number.isFinite(naturalHeight) && naturalWidth > 0 && naturalHeight > 0 && width > 0 && height > 0) {
          const fit = Math.min(1, width / naturalWidth, height / naturalHeight);
          imageWidth = naturalWidth * fit;
          imageHeight = naturalHeight * fit;
          this.activeImage.style.setProperty('--hissa-fit-width', `${imageWidth}px`);
          this.activeImage.style.setProperty('--hissa-fit-height', `${imageHeight}px`);
        }
        const limitX = Math.max(0, (imageWidth * this.zoom - width) / 2);
        const limitY = Math.max(0, (imageHeight * this.zoom - height) / 2);
        this.pan.x = Math.max(-limitX, Math.min(limitX, this.pan.x));
        this.pan.y = Math.max(-limitY, Math.min(limitY, this.pan.y));
      }
      this.stage.style.setProperty('--hissa-zoom', this.zoom);
      this.stage.style.setProperty('--hissa-pan-x', `${this.pan.x}px`);
      this.stage.style.setProperty('--hissa-pan-y', `${this.pan.y}px`);
      this.toggleAttribute('data-zoomed', this.zoom > 1);
      const out = this.querySelector('[data-hissa-zoom-out]');
      const plus = this.querySelector('[data-hissa-zoom-in]');
      const status = this.querySelector('[data-hissa-zoom-status]');
      if (out) out.disabled = this.zoom <= 1;
      if (plus) plus.disabled = this.zoom >= 4;
      if (status) status.textContent = `${Math.round(this.zoom * 100)}%`;
    }
    pointerDown(event) {
      if (!this.activeImage || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.stage.setPointerCapture(event.pointerId);
      if (this.pointers.size === 1) {
        this.swipeStart = { x: event.clientX, y: event.clientY };
        this.hadPinch = false;
      } else {
        this.hadPinch = true;
      }
      const points = Array.from(this.pointers.values());
      this.gesture = points.length >= 2 ? {
        distance: Math.max(1, Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)),
        zoom: this.zoom,
      } : null;
      this.lastPoint = { x: event.clientX, y: event.clientY };
    }
    pointerEnd(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const start = this.swipeStart;
      const canSwipe = event.type === 'pointerup' && event.pointerType !== 'mouse' && this.pointers.size === 1 && !this.hadPinch && this.zoom === 1 && start;
      this.pointers.delete(event.pointerId);
      this.gesture = null;
      this.lastPoint = Array.from(this.pointers.values())[0] || null;
      if (!this.pointers.size) this.swipeStart = null;
      if (canSwipe) {
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3) this.navigate(dx < 0 ? 1 : -1);
      }
    }
    pointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.activeImage) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const points = Array.from(this.pointers.values());
      if (points.length >= 2) {
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        if (!this.gesture) this.gesture = { distance: Math.max(distance, 1), zoom: this.zoom };
        this.setZoom(this.gesture.zoom * distance / this.gesture.distance);
      } else if (this.zoom > 1 && this.lastPoint) {
        this.pan.x += event.clientX - this.lastPoint.x;
        this.pan.y += event.clientY - this.lastPoint.y;
        this.paintZoom();
      }
      this.lastPoint = { x: event.clientX, y: event.clientY };
    }
  });
}
